import { createReadStream, createWriteStream } from 'node:fs'
import { mkdir, readdir, readFile, stat } from 'node:fs/promises'
import { basename, dirname, join, posix, resolve } from 'node:path'
import { Readable } from 'node:stream'
import { pipeline } from 'node:stream/promises'
import { Command } from 'commander'
import { createApiClient, type ApiClient } from '../http.js'
import { files } from '../api/catalog.js'
import { call, callRaw, fillPath, pageItems, type PagedResult } from '../api/client.js'
import { resolveWorkspaceId } from '../context.js'
import { output, outputRecord } from '../output.js'

export interface FileDto {
  path: string
  name: string
  mimeType: string
  hash?: string
  version?: string
  etag?: string
  children?: PagedResult<FileDto>
  size?: number | null
  nodeId?: string
  [key: string]: unknown
}

interface FileFlags {
  workspace?: string
  branch: string
  json?: boolean
}

const normalizeRemote = (path: string): string => {
  const trimmed = path.replace(/^\/+/, '').replace(/\/+$/, '')
  return trimmed
}

async function uploadWithHeaders(
  api: ApiClient,
  workspaceId: string,
  remotePath: string,
  localPath: string,
  options: { branch: string; contentType?: string; autoExtract?: boolean },
): Promise<FileDto> {
  // Raw body upload with branch/extract headers (multipart not required by the controller).
  const stream = Readable.toWeb(createReadStream(localPath)) as unknown as ReadableStream
  const res = await api.requestRaw('POST', fillPath(files.upload.path, { path: normalizeRemote(remotePath) }), {
    scopes: [...files.upload.scopes],
    tokenWorkspaceId: workspaceId,
    rawBody: stream,
    contentType: options.contentType ?? 'application/octet-stream',
    headers: {
      'X-Branch': options.branch,
      'Content-Disposition': `attachment; filename="${basename(localPath)}"`,
      ...(options.autoExtract ? { 'X-Auto-Extract': 'true' } : {}),
    },
  })
  return (await res.json()) as FileDto
}

export function registerFiles(program: Command): void {
  const filesCmd = program.command('files').description('Work with workspace files (the VFS)')

  filesCmd
    .command('ls [path]')
    .description('List a directory in the workspace')
    .option('--workspace <workspaceId>')
    .option('--branch <branch>', 'workspace branch', 'main')
    .option('--json', 'output JSON')
    .action(async (path: string | undefined, options: FileFlags) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      const result = await call<FileDto>(api, files.get, {
        params: { path: normalizeRemote(path ?? '/') },
        workspaceId,
        query: { branch: options.branch, PageNo: 1, PageSize: 100 },
      })
      const entries = pageItems(result.children)
      if (entries.length === 0 && result.name) {
        outputRecord(result as Record<string, unknown>, options)
        return
      }
      output(
        entries.map((entry) => ({ ...entry, size: entry.size ?? '' })),
        { name: 'Name', mimeType: 'Type', size: 'Size', path: 'Path' },
        options,
        result,
      )
    })

  filesCmd
    .command('stat <path>')
    .description('Show file metadata')
    .option('--workspace <workspaceId>')
    .option('--branch <branch>', 'workspace branch', 'main')
    .option('--json', 'output JSON')
    .action(async (path: string, options: FileFlags) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      const result = await call<FileDto>(api, files.get, {
        params: { path: normalizeRemote(path) },
        workspaceId,
        query: { branch: options.branch },
      })
      const { children, ...rest } = result
      outputRecord(rest as Record<string, unknown>, options)
    })

  filesCmd
    .command('cat <path>')
    .description('Print raw file content to stdout')
    .option('--workspace <workspaceId>')
    .option('--branch <branch>', 'workspace branch', 'main')
    .action(async (path: string, options: FileFlags) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      const res = await callRaw(api, files.raw, {
        params: { path: normalizeRemote(path) },
        workspaceId,
        query: { branch: options.branch },
      })
      if (!res.body) return
      await pipeline(Readable.fromWeb(res.body as import('node:stream/web').ReadableStream), process.stdout, { end: false })
    })

  filesCmd
    .command('download <remotePath> [localPath]')
    .description('Download a file (or directory as an archive with --format)')
    .option('--workspace <workspaceId>')
    .option('--branch <branch>', 'workspace branch', 'main')
    .option('--format <format>', 'archive format for directories: Zip | TarGz')
    .action(async (remotePath: string, localPath: string | undefined, options: FileFlags & { format?: string }) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      const res = await callRaw(api, files.raw, {
        params: { path: normalizeRemote(remotePath) },
        workspaceId,
        query: { branch: options.branch, format: options.format },
      })
      const suffix = options.format === 'Zip' ? '.zip' : options.format === 'TarGz' ? '.tar.gz' : ''
      const out = resolve(localPath ?? `${basename(normalizeRemote(remotePath)) || 'download'}${suffix}`)
      if (!res.body) throw new Error('Empty response.')
      await mkdir(dirname(out), { recursive: true })
      await pipeline(Readable.fromWeb(res.body as import('node:stream/web').ReadableStream), createWriteStream(out))
      console.log(`Downloaded ${remotePath} → ${out}`)
    })

  filesCmd
    .command('upload <localPath> [remotePath]')
    .description('Upload a file or directory (directories upload file-by-file)')
    .option('--workspace <workspaceId>')
    .option('--branch <branch>', 'workspace branch', 'main')
    .option('--content-type <type>', 'content type for a single-file upload')
    .option('--extract', 'archive uploads: extract server-side into the target directory (X-Auto-Extract)')
    .action(async (localPath: string, remotePath: string | undefined, options: FileFlags & { contentType?: string; extract?: boolean }) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      const local = resolve(localPath)
      const info = await stat(local)

      if (info.isDirectory()) {
        const remoteBase = normalizeRemote(remotePath ?? basename(local))
        const uploaded = await uploadDirectory(api, workspaceId, local, remoteBase, options.branch)
        console.log(`Uploaded ${uploaded} file(s) to /${remoteBase}.`)
        return
      }

      const remote = remotePath && remotePath.endsWith('/') ? posix.join(normalizeRemote(remotePath), basename(local)) : normalizeRemote(remotePath ?? basename(local))
      const result = await uploadWithHeaders(api, workspaceId, remote, local, {
        branch: options.branch,
        contentType: options.contentType,
        autoExtract: options.extract,
      })
      console.log(`Uploaded ${localPath} → /${result.path ?? remote}`)
    })

  filesCmd
    .command('rm <path>')
    .description('Delete a file or directory')
    .option('--workspace <workspaceId>')
    .option('--branch <branch>', 'workspace branch', 'main')
    .option('--recursive', 'delete directory contents recursively')
    .action(async (path: string, options: FileFlags & { recursive?: boolean }) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      await api.requestRaw('DELETE', fillPath(files.delete.path, { path: normalizeRemote(path) }), {
        scopes: [...files.delete.scopes],
        tokenWorkspaceId: workspaceId,
        query: { branch: options.branch },
        headers: options.recursive ? { 'X-Delete-Mode': 'Deep' } : {},
      })
      console.log(`Deleted /${normalizeRemote(path)}.`)
    })

  filesCmd
    .command('mv <fromPath> <toPath>')
    .description('Move or rename a file')
    .option('--workspace <workspaceId>')
    .option('--branch <branch>', 'workspace branch', 'main')
    .action(async (fromPath: string, toPath: string, options: FileFlags) => {
      const workspaceId = (await resolveWorkspaceId(options))!
      const api = await createApiClient()
      await call(api, files.update, {
        params: { path: normalizeRemote(fromPath) },
        workspaceId,
        body: { branch: options.branch, path: normalizeRemote(fromPath), toPath: normalizeRemote(toPath) },
      })
      console.log(`Moved /${normalizeRemote(fromPath)} → /${normalizeRemote(toPath)}.`)
    })
}

async function uploadDirectory(api: ApiClient, workspaceId: string, localDir: string, remoteBase: string, branch: string): Promise<number> {
  let count = 0
  const entries = await readdir(localDir, { withFileTypes: true })
  for (const entry of entries) {
    if (entry.name.startsWith('.git') || entry.name === 'node_modules' || entry.name === '__pycache__') continue
    const localEntry = join(localDir, entry.name)
    const remoteEntry = posix.join(remoteBase, entry.name)
    if (entry.isDirectory()) {
      count += await uploadDirectory(api, workspaceId, localEntry, remoteEntry, branch)
    } else if (entry.isFile()) {
      await uploadWithHeaders(api, workspaceId, remoteEntry, localEntry, { branch })
      console.log(`  /${remoteEntry}`)
      count += 1
    }
  }
  return count
}

/** Read a remote file fully into memory (helper for other commands). */
export async function readRemoteFile(api: ApiClient, workspaceId: string, path: string, branch: string): Promise<Buffer> {
  const res = await callRaw(api, files.raw, { params: { path: normalizeRemote(path) }, workspaceId, query: { branch } })
  return Buffer.from(await res.arrayBuffer())
}
